import { ArrowUp, ChartColumn, CornerDownRight, Lightbulb } from 'lucide-react';
import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  Persona,
  ResearchPollDraft,
  ResearchPollFormat,
} from '@repo/contracts';
import { Tooltip } from '@repo/ui/tooltip';

import { trackEvent } from '../analytics/posthog';
import { LOOKALIKES_ENABLED } from '../persona-lookalikes/lookalikes-enabled';
import { PersonaAvatar } from '../persona/persona-avatar';
import { useResearchRoomStore } from '../store/research-room-store';
import { LookalikePollEditor } from './lookalike-poll-editor';
import {
  activeMention,
  handleOf,
  insertMention,
  matchMentions,
  mentionedIds,
} from './mentions';
import { draftProblem, initialOptions } from './poll-draft';
import {
  MAX_POLL_QUESTION,
  type PollDraftState,
  PollEditor,
} from './poll-editor';
import { PollTypePicker } from './poll-type-picker';
import { useQuestionShape } from './use-question-shape';

const DEFAULT_LOOKALIKE_COUNT = 1000;

/** How long the composer sits empty after a round before the bulb lights:
 *  long enough to read the answers first, which is the point. */
const IDLE_MS = 25_000;

/**
 * Where the researcher asks.
 *
 * Everyone by default; `@` someone to ask only them, Discord-style — typing
 * `@` opens the room's people, filtered as you type, and picking one writes
 * `@Name` into the question. Who is asked is read from the text on send (see
 * `mentions.ts`), and a "To" line above the field says so before it goes.
 *
 * Enter sends and Shift+Enter breaks a line, except while the list is open,
 * when Enter and Tab pick and the arrows move.
 *
 * Floto's ideas for what to ask next wait behind a button rather than under
 * every round: shown each time, they were easier to click than to think
 * past, and the researcher stopped leading the conversation.
 *
 * Polls likewise: the chart button beside the bulb opens them, and when the
 * question reads like one the button jumps and the picker rings that kind —
 * a suggestion, never a poll made for you.
 */
export function Composer({
  busy,
  ideas,
  inputRef,
  onAskLookalikePoll,
  onPickIdea,
  onSend,
  personas,
}: {
  busy: boolean;
  /** What Floto would ask next, from the latest round. */
  ideas: string[];
  inputRef: RefObject<HTMLTextAreaElement | null>;
  /** Puts an idea in the field, to be read, edited or sent. */
  onPickIdea: (question: string) => void;
  onAskLookalikePoll: (
    personaId: string,
    question: string,
    count?: number,
  ) => void;
  onSend: (
    text: string,
    targets: string[],
    skipCheck: boolean,
    poll?: ResearchPollDraft,
  ) => void;
  personas: Persona[];
}) {
  const draft = useResearchRoomStore((s) => s.draft);
  const setDraft = useResearchRoomStore((s) => s.setDraft);
  const vetted = useResearchRoomStore((s) => s.vetted);
  const independent = useResearchRoomStore((s) => s.independent);

  const [caret, setCaret] = useState(0);
  const [ideasOpen, setIdeasOpen] = useState(false);
  // The poll button's first step, the kind of poll — before there is one.
  const [pickerOpen, setPickerOpen] = useState(false);
  // New ideas belong to a new round; an open list must not swap under you.
  const ideasKey = ideas.join('\n');
  useEffect(() => {
    setIdeasOpen(false);
  }, [ideasKey]);
  // An open list goes the way a menu does: a press anywhere outside the
  // composer, Escape wherever focus is, or the room taking a question,
  // closes it. Escape is claimed (`preventDefault`), so the board — which
  // leaves the room on an Escape nobody else wanted — leaves it be.
  const rootRef = useRef<HTMLDivElement>(null);
  const listOpen = ideasOpen || pickerOpen;
  useEffect(() => {
    if (!listOpen) return;
    const close = () => {
      setIdeasOpen(false);
      setPickerOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        rootRef.current?.contains(event.target)
      ) {
        return;
      }
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      close();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [listOpen]);
  useEffect(() => {
    if (busy) {
      setIdeasOpen(false);
      setPickerOpen(false);
    }
  }, [busy]);

  // The bulb lights once per round, if the researcher has typed nothing for
  // a while: a hint that ideas are there, never the ideas themselves. Just
  // the glow; no "Need an idea?" pill pops up over it.
  const [lit, setLit] = useState(false);
  const litFor = useRef<string | null>(null);
  const idle =
    ideas.length > 0 && !busy && !ideasOpen && draft.trim().length === 0;
  useEffect(() => {
    if (!idle) {
      setLit(false);
      return;
    }
    if (litFor.current === ideasKey) return;
    const timer = setTimeout(() => {
      litFor.current = ideasKey;
      setLit(true);
    }, IDLE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [idle, ideasKey]);
  const [dismissed, setDismissed] = useState<number | null>(null);
  const [active, setActive] = useState(0);

  // The poll the question will go as, or null for an ordinary question.
  const [poll, setPoll] = useState<PollDraftState | null>(null);
  // The poll's question, in its own field while the poll is open.
  const [pollQuestion, setPollQuestion] = useState('');
  // Set by ✕ on the poll card: the researcher wants a plain question, so the
  // shape reading stops suggesting a poll until the box is cleared.
  const [plain, setPlain] = useState(false);
  const [tried, setTried] = useState(false);
  const shape = useQuestionShape(draft, !busy && !plain);

  // A lookalike poll: the same "ask as a poll" card, but who answers is a
  // simulated population of one persona rather than the room. A tab inside
  // the poll card switches between the two — not a separate button, since
  // both are just a different destination for the same question.
  const [lookalikeMode, setLookalikeMode] = useState(false);
  // Pre-picked with the first seated persona rather than left empty: an
  // empty choice would leave Send silently disabled with nothing to explain
  // why, and re-picking whoever is already in the room is the common case.
  const [lookalikePersonaId, setLookalikePersonaId] = useState<string | null>(
    null,
  );
  const [lookalikeCount, setLookalikeCount] = useState(DEFAULT_LOOKALIKE_COUNT);
  const toLookalikes = () => {
    trackEvent('composer_mode_changed', { mode: 'lookalikes' });
    setPoll(null);
    setPickerOpen(false);
    setPlain(true);
    setTried(false);
    setLookalikeMode(true);
    setLookalikePersonaId((current) => current ?? personas[0]?.id ?? null);
  };
  const toRoomPoll = () => {
    trackEvent('composer_mode_changed', { mode: 'room' });
    setLookalikeMode(false);
    openPoll(suggested ?? 'choice');
  };
  const closeEditor = () => {
    setLookalikeMode(false);
    setLookalikePersonaId(null);
    setLookalikeCount(DEFAULT_LOOKALIKE_COUNT);
    // ✕ means "ask it plain": the question goes back where it came from.
    if (poll) {
      setDraft(pollQuestion);
      setCaret(pollQuestion.length);
    }
    setPollQuestion('');
    setPoll(null);
    setPickerOpen(false);
    setPlain(true);
  };

  // "Edit poll" on a card: the question and its poll, into the editor.
  const seededPoll = useResearchRoomStore((s) => s.seededPoll);
  const takeSeededPoll = useResearchRoomStore((s) => s.takeSeededPoll);
  useEffect(() => {
    if (!seededPoll) return;
    const seeded = takeSeededPoll();
    if (!seeded) return;
    setPlain(false);
    setTried(false);
    setPickerOpen(false);
    // The card's question came in through the box; it belongs to the poll.
    setPollQuestion(useResearchRoomStore.getState().draft);
    setDraft('');
    setPoll({ ...seeded, touched: true });
  }, [seededPoll, takeSeededPoll, setDraft]);

  // What the question reads as, to suggest — the button jumps and the
  // picker rings it. Only while there is no poll yet, and not once the
  // researcher has said "plain".
  const suggested =
    !plain && !poll && !lookalikeMode && draft.trim().length > 0
      ? (shape.committed ?? shape.hint)
      : null;

  // Until an option is edited by hand, the options follow the question.
  useEffect(() => {
    setPoll((current) =>
      current && !current.touched
        ? { ...current, options: initialOptions(current.format, pollQuestion) }
        : current,
    );
  }, [pollQuestion]);

  // Opening a poll moves what was typed into it, so the question lives in
  // one place — the poll's own field — rather than in both.
  const openPoll = (format: ResearchPollFormat) => {
    const question = poll ? pollQuestion : draft;
    setPickerOpen(false);
    setPlain(false);
    setTried(false);
    setPollQuestion(question);
    if (!poll) {
      setDraft('');
      setCaret(0);
    }
    setPoll({
      format,
      options: initialOptions(format, question),
      touched: false,
    });
    setLookalikeMode(false);
  };

  const pollOpen = pickerOpen || poll !== null || lookalikeMode;
  // Where the popover's tail sits: under the poll button, wherever the bulb
  // beside it leaves it.
  const pollButtonRef = useRef<HTMLButtonElement>(null);
  const [tailAt, setTailAt] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const button = pollButtonRef.current;
    const root = rootRef.current;
    if (!pollOpen || !button || !root) return;
    const b = button.getBoundingClientRect();
    setTailAt(b.left + b.width / 2 - root.getBoundingClientRect().left);
  }, [pollOpen, ideas.length]);

  const mention = activeMention(draft, caret);
  const matches = mention ? matchMentions(mention.query, personas) : [];
  const open =
    mention !== null && matches.length > 0 && dismissed !== mention.start;

  // The question about to go: the poll's, while there is one.
  const asked = poll ? pollQuestion : draft;
  const targets = useMemo(
    () => mentionedIds(asked, personas),
    [asked, personas],
  );
  const addressed = targets.flatMap(
    (id) => personas.find((persona) => persona.id === id) ?? [],
  );

  const text = asked.trim();
  const canSend =
    !busy &&
    text.length >= 2 &&
    (!lookalikeMode || lookalikePersonaId !== null);
  const problem = poll
    ? text.length > MAX_POLL_QUESTION
      ? `Keep the question under ${String(MAX_POLL_QUESTION)} characters`
      : draftProblem(poll.format, poll.options)
    : null;

  // A new query starts at the top of the list.
  const query = mention?.query;
  useEffect(() => {
    setActive(0);
  }, [query]);

  // Grow with the text up to the field's own `max-h`, past which it scrolls.
  useEffect(() => {
    const field = inputRef.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${String(field.scrollHeight)}px`;
  }, [draft, inputRef]);

  // Where the caret goes once a picked mention is in the text. Applied in a
  // layout effect — synchronously after React writes the new value, before
  // the next keystroke — because a frame later is already too late for a
  // fast typist: the first letters land after the mention and the rest jump
  // back in front of them.
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const at = pendingCaret.current;
    const field = inputRef.current;
    if (at === null || !field) return;
    pendingCaret.current = null;
    field.focus();
    field.setSelectionRange(at, at);
  }, [draft, inputRef]);

  const syncCaret = () => {
    setCaret(inputRef.current?.selectionStart ?? draft.length);
  };

  const pick = (persona: Persona) => {
    if (!mention) return;
    trackEvent('persona_mentioned', { personas: personas.length });
    const next = insertMention(draft, mention, handleOf(persona, personas));
    pendingCaret.current = next.caret;
    setDraft(next.text);
    setCaret(next.caret);
  };

  // No waiting on the shape reading here: a plain question that reads as a
  // poll is turned into one by the check it gets on send, so pressing Enter
  // early costs nothing.
  const send = () => {
    if (!canSend) return;
    if (lookalikeMode) {
      if (!lookalikePersonaId) return;
      // The count only matters when the persona has no spec yet — the
      // server ignores it once one exists (see its own doc on why).
      onAskLookalikePoll(lookalikePersonaId, text, lookalikeCount);
      setDraft('');
      setCaret(0);
      closeEditor();
      return;
    }
    if (problem) {
      setTried(true);
      return;
    }
    // Floto's own question, unedited, has nothing to check.
    const unedited = vetted !== null && text === vetted.trim();
    // Counted where every question lands (useResearchRoom's `ask`), with
    // `idea` for Floto's own sent as written — see the panel's onSend.
    onSend(
      text,
      targets,
      unedited,
      poll
        ? {
            format: poll.format,
            options: poll.options.map((o) => o.trim()).filter(Boolean),
          }
        : undefined,
    );
    setDraft('');
    setCaret(0);
    setPoll(null);
    setPollQuestion('');
    setPlain(false);
    setTried(false);
  };

  return (
    <div className="relative" ref={rootRef}>
      {ideasOpen && ideas.length > 0 && !open ? (
        <div
          aria-label="Ideas for what to ask next"
          className="absolute right-0 bottom-full left-0 z-10 mb-2 rounded-card bg-surface p-2 shadow-popover ring-1 ring-hairline duration-150 animate-in fade-in slide-in-from-bottom-1"
          id="idea-list"
        >
          <p className="px-1.5 pt-0.5 pb-1.5 text-small-label text-text-muted uppercase">
            Suggested questions
          </p>
          <div className="flex flex-col gap-1">
            {ideas.map((question) => (
              <button
                className="group flex cursor-pointer items-start gap-2 rounded-button px-2.5 py-2 text-left text-caption leading-snug text-text-primary transition hover:bg-surface-hover"
                key={question}
                onClick={() => {
                  trackEvent('question_idea_picked', { ideas: ideas.length });
                  setIdeasOpen(false);
                  onPickIdea(question);
                }}
                type="button"
              >
                <CornerDownRight
                  aria-hidden="true"
                  className="mt-0.5 size-3.5 shrink-0 text-text-muted transition group-hover:text-brand"
                />
                {question}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {open ? (
        <div
          aria-label="People in the room"
          className="absolute right-0 bottom-full left-0 z-10 mb-2 rounded-card bg-surface p-1.5 shadow-popover ring-1 ring-hairline duration-150 animate-in fade-in slide-in-from-bottom-1"
          id="mention-list"
          role="listbox"
        >
          {matches.map((persona, i) => (
            <button
              aria-selected={i === active}
              className={[
                'flex w-full cursor-pointer items-center gap-2.5 rounded-button px-2.5 py-2 text-left transition',
                i === active ? 'bg-surface-hover' : 'hover:bg-surface-hover',
              ].join(' ')}
              key={persona.id}
              // mousedown, not click: a click would blur the field first and
              // lose the caret the mention is inserted at.
              onMouseDown={(event) => {
                event.preventDefault();
                pick(persona);
              }}
              onMouseEnter={() => {
                setActive(i);
              }}
              role="option"
              type="button"
            >
              <PersonaAvatar persona={persona} size="sm" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-ui font-medium text-text-primary">
                  {persona.name}
                </span>
                <span className="block truncate text-tag text-text-muted">
                  {persona.title}
                </span>
              </span>
              <span className="shrink-0 text-tag text-text-muted">
                @{handleOf(persona, personas)}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {addressed.length > 0 ? (
        <p className="mb-2 flex items-center gap-1.5 px-1 text-caption text-text-secondary duration-150 animate-in fade-in">
          <span className="text-text-muted">To</span>
          <span className="flex -space-x-1">
            {addressed.map((persona) => (
              <PersonaAvatar
                className="ring-2 ring-surface"
                key={persona.id}
                persona={persona}
                size="sm"
              />
            ))}
          </span>
          <span className="truncate font-medium text-text-primary">
            {addressed.map((persona) => persona.name.split(' ')[0]).join(', ')}
          </span>
          <span className="shrink-0 text-text-muted">only</span>
        </p>
      ) : null}

      {pickerOpen || poll || lookalikeMode ? (
        <div
          className="absolute right-0 bottom-full left-0 z-10 mb-3 rounded-panel bg-surface shadow-overlay ring-1 ring-hairline duration-200 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none"
          id="poll-popover"
        >
          {/* Only the room for V1: with lookalikes off there is one
              destination, and a single tab is not a choice. */}
          {LOOKALIKES_ENABLED && !pickerOpen ? (
            <div className="flex gap-1 px-4 pt-3" role="tablist">
              <button
                aria-selected={!lookalikeMode}
                className={[
                  'h-6 shrink-0 cursor-pointer rounded-button px-2 text-tag font-semibold whitespace-nowrap transition',
                  !lookalikeMode
                    ? 'bg-ink text-white'
                    : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                ].join(' ')}
                onClick={toRoomPoll}
                role="tab"
                type="button"
              >
                Ask the room
              </button>
              <button
                aria-selected={lookalikeMode}
                className={[
                  'h-6 shrink-0 cursor-pointer rounded-button px-2 text-tag font-semibold whitespace-nowrap transition',
                  lookalikeMode
                    ? 'bg-ink text-white'
                    : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                ].join(' ')}
                onClick={toLookalikes}
                role="tab"
                type="button"
              >
                Ask lookalikes
              </button>
            </div>
          ) : null}

          {pickerOpen ? (
            <PollTypePicker
              onClose={() => {
                setPickerOpen(false);
              }}
              onPick={(format) => {
                trackEvent('poll_editor_opened', {
                  format,
                  recommended: suggested ?? undefined,
                  source: format === suggested ? 'recommended' : 'picker',
                });
                openPoll(format);
              }}
              recommended={suggested}
            />
          ) : lookalikeMode ? (
            <div className="p-3">
              <LookalikePollEditor
                count={lookalikeCount}
                onClose={() => {
                  closeEditor();
                  inputRef.current?.focus();
                }}
                onCountChange={setLookalikeCount}
                onPersonaChange={setLookalikePersonaId}
                personaId={lookalikePersonaId}
                personas={personas}
              />
            </div>
          ) : poll ? (
            <PollEditor
              canStart={canSend}
              draft={poll}
              onChange={(options) => {
                setPoll({ ...poll, options, touched: true });
              }}
              onClose={() => {
                closeEditor();
                inputRef.current?.focus();
              }}
              onFormat={(format) => {
                setPoll({
                  format,
                  // Items carry over between a pick and a ranking; any other
                  // switch starts from the question again.
                  options:
                    poll.touched &&
                    (format === 'choice' || format === 'ranking') &&
                    (poll.format === 'choice' || poll.format === 'ranking')
                      ? poll.options
                      : initialOptions(format, pollQuestion),
                  touched:
                    poll.touched && format !== 'rating' && format !== 'yes-no',
                });
              }}
              onQuestion={setPollQuestion}
              onStart={send}
              problem={tried ? problem : null}
              question={pollQuestion}
            />
          ) : null}

          {/* The popover's tail, under the poll button. */}
          <span
            aria-hidden="true"
            className="absolute top-full size-3 -translate-x-1/2 -translate-y-1.5 rotate-45 bg-surface"
            style={{ left: tailAt }}
          />
        </div>
      ) : null}

      {/* The same box everywhere: the text on top at full width, growing a
          line at a time, and one row of buttons under it — poll and ideas on
          the left, send on the right. Nothing moves as you type. */}
      <div className="flex flex-col gap-0.5 rounded-card bg-surface p-1.5 ring-1 ring-hairline-alt transition focus-within:ring-2 focus-within:ring-brand/40">
        <textarea
          aria-autocomplete="list"
          aria-controls={open ? 'mention-list' : undefined}
          aria-expanded={open}
          aria-label="Ask the room"
          className="max-h-32 min-h-9 w-full resize-none bg-transparent px-2 py-2 text-ui leading-5 text-text-primary outline-none placeholder:text-text-muted"
          onChange={(event) => {
            // Writing their own question: the list is in the way.
            setIdeasOpen(false);
            setDraft(event.target.value);
            setCaret(event.target.selectionStart);
            setDismissed(null);
            // A cleared box is a fresh start: the shape may take over again.
            // Not while asking lookalikes, though — clearing the question
            // must not silently switch the tab back to a room poll.
            if (!event.target.value.trim() && !lookalikeMode) {
              setPlain(false);
              setPoll(null);
            }
          }}
          onClick={syncCaret}
          onKeyDown={(event) => {
            if (open && mention) {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setActive((i) => (i + step + matches.length) % matches.length);
                return;
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                const chosen = matches[active];
                if (chosen) pick(chosen);
                return;
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                setDismissed(mention.start);
                return;
              }
            }
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              send();
            }
          }}
          onKeyUp={(event) => {
            // Arrow keys and Home/End move the caret without changing the
            // text, and a mention is only "being typed" where the caret is.
            if (
              event.key.startsWith('Arrow') ||
              event.key === 'Home' ||
              event.key === 'End'
            ) {
              syncCaret();
            }
          }}
          placeholder={
            busy
              ? 'Waiting for the room to finish…'
              : lookalikeMode
                ? 'The yes / no question'
                : poll
                  ? 'Finish your poll above'
                  : independent
                    ? 'Everyone answers without hearing the others'
                    : 'Ask everyone, or type @ to ask someone'
          }
          // The poll has its own question field; this one waits.
          disabled={poll !== null}
          ref={inputRef}
          rows={1}
          value={draft}
        />
        <div className="flex items-center">
          <span className="flex gap-1">
            <Tooltip label="Create a poll">
              <button
                aria-controls={pollOpen ? 'poll-popover' : undefined}
                aria-expanded={pollOpen}
                aria-label={
                  suggested ? 'Create a poll (suggested)' : 'Create a poll'
                }
                className={[
                  'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-button transition',
                  pollOpen
                    ? 'bg-brand text-white hover:bg-brand-alt'
                    : suggested
                      ? 'bg-brand/10 text-brand ring-1 ring-brand/30 hover:bg-brand/15'
                      : 'text-text-muted hover:bg-surface-hover hover:text-text-primary',
                ].join(' ')}
                disabled={busy}
                onClick={() => {
                  if (poll || lookalikeMode) {
                    closeEditor();
                    inputRef.current?.focus();
                    return;
                  }
                  if (!pickerOpen) {
                    trackEvent('poll_picker_opened', {
                      suggested: suggested ?? undefined,
                    });
                  }
                  setIdeasOpen(false);
                  setPickerOpen(!pickerOpen);
                }}
                ref={pollButtonRef}
                type="button"
              >
                <ChartColumn
                  aria-hidden="true"
                  className={[
                    'size-4',
                    suggested && !pollOpen
                      ? 'duration-500 animate-in zoom-in-50 spin-in-12 motion-reduce:animate-none'
                      : '',
                  ].join(' ')}
                  // Remounts on each new suggestion, so the jump plays again.
                  key={suggested ?? 'none'}
                />
              </button>
            </Tooltip>
            {ideas.length > 0 ? (
              <span className="relative flex">
                <Tooltip label="Explore what to ask next">
                  <button
                    aria-controls={ideasOpen ? 'idea-list' : undefined}
                    aria-expanded={ideasOpen}
                    aria-label="Explore what to ask next"
                    className={[
                      'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-button transition',
                      ideasOpen
                        ? 'bg-brand/10 text-brand hover:bg-brand/15'
                        : lit
                          ? 'bg-idea-soft text-idea ring-1 ring-idea/30 hover:bg-idea-soft'
                          : 'text-text-muted hover:bg-surface-hover hover:text-text-primary',
                    ].join(' ')}
                    onClick={() => {
                      if (!ideasOpen) {
                        trackEvent('question_ideas_opened', {
                          ideas: ideas.length,
                          lit,
                          source: 'bulb',
                        });
                      }
                      setPickerOpen(false);
                      setIdeasOpen(!ideasOpen);
                    }}
                    type="button"
                  >
                    <Lightbulb
                      aria-hidden="true"
                      className={[
                        'size-4',
                        lit
                          ? 'fill-idea/25 duration-500 animate-in zoom-in-50 spin-in-12 motion-reduce:animate-none'
                          : '',
                      ].join(' ')}
                      // Remounts when it lights, so the pop plays each time.
                      key={lit ? 'lit' : 'unlit'}
                    />
                  </button>
                </Tooltip>
              </span>
            ) : null}
          </span>
          <button
            aria-label="Send"
            className="ml-auto flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-button bg-brand text-white transition hover:bg-brand-alt active:scale-[0.96] disabled:cursor-default disabled:bg-hairline disabled:text-text-muted"
            disabled={!canSend || poll !== null}
            onClick={() => {
              send();
            }}
            type="button"
          >
            <ArrowUp aria-hidden="true" className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
